import { motion } from "motion/react";
import { Package, Feather, Calendar, Gift } from "lucide-react";
import WaitlistForm from "./WaitlistForm";

const Section = ({ children, id, className = "" }) => (
  <section 
    id={id}
    className={`px-8 py-24 md:px-20 md:py-32 ${className}`}
    data-testid={`section-${id}`}
  >
    <div className="max-w-7xl mx-auto">
      {children}
    </div>
  </section>
);

export const SubscriptionBoxPage = () => {
  const whatsInside = [
    {
      icon: Feather,
      title: "Considered stationery",
      desc: "Paper and ink worth slowing down for."
    },
    {
      icon: Calendar,
      title: "A monthly theme & prompt",
      desc: "A reason to write, and someone to write to."
    },
    {
      icon: Gift,
      title: "Curated extras",
      desc: "Small treasures to make the ritual feel special."
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Section id="box" className="bg-lws-paper">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <span className="text-xs uppercase tracking-[0.4em] text-lws-amber font-semibold">
              Coming Soon — The Letter Box
            </span>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif leading-[1.1] text-lws-ink">
              Letterbox joy,{" "}
              <span className="italic text-lws-green">delivered.</span>
            </h1>
            
            <p className="text-xl text-lws-taupe font-serif leading-relaxed max-w-xl">
              A considered box of correspondence — fine paper, good ink, a monthly theme, 
              and a prompt to begin. Delivered to your door so the only thing left to do 
              is sit down and write.
            </p>
            
            <p className="text-lws-taupe">
              We're making the first one now. Join the list to be there when it ships.
            </p>

            {/* What's Inside */}
            <div className="space-y-6 pt-4">
              <h3 className="text-xs uppercase tracking-[0.3em] text-lws-ink font-semibold">
                What's Inside
              </h3>
              {whatsInside.map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-lws-green/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-lws-green" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lws-ink">{item.title}</h4>
                    <p className="text-sm text-lws-taupe">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-[4/5] bg-lws-taupe/10 overflow-hidden relative">
              <img 
                src="https://images.unsplash.com/photo-1517971071642-34a2d3ecc9cd?w=800&h=1000&fit=crop"
                alt="The Letter Box - Stationery Subscription"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {/* Overlay Card */}
              <div className="absolute bottom-8 left-8 right-8">
                <div className="bg-lws-paper/95 backdrop-blur-sm p-8 shadow-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <Package className="w-6 h-6 text-lws-green" />
                    <span className="text-xs uppercase tracking-[0.2em] text-lws-amber font-bold">
                      Subscription Box
                    </span>
                  </div>
                  <h3 className="text-2xl font-serif text-lws-ink mb-2">The Letter Box</h3>
                  <p className="text-sm text-lws-taupe font-serif italic">
                    "Letterbox joy, delivered."
                  </p>
                </div>
              </div>
            </div>
            
            {/* Decorative Element */}
            <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-lws-green/20 -z-10" />
          </motion.div>
        </div>
      </Section>

      {/* Waitlist Form */}
      <WaitlistForm 
        title="Be the first to know."
        subtitle="Join the waitlist and we'll let you know as soon as The Letter Box is ready to ship."
        tag="subbox"
      />

      {/* Additional Info Section */}
      <Section id="box-details" className="bg-lws-paper">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-serif text-lws-ink">
            For the slow correspondent.
          </h2>
          <p className="text-lg text-lws-taupe font-serif leading-relaxed">
            In a world of instant messages and endless notifications, The Letter Box 
            is an invitation to pause. Each month, receive everything you need to write 
            one meaningful letter — and remember why correspondence matters.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8">
            <div className="text-center">
              <div className="text-4xl font-serif text-lws-green mb-2">01</div>
              <h4 className="font-semibold text-lws-ink mb-1">Receive</h4>
              <p className="text-sm text-lws-taupe">Your box arrives at your door</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-serif text-lws-green mb-2">02</div>
              <h4 className="font-semibold text-lws-ink mb-1">Reflect</h4>
              <p className="text-sm text-lws-taupe">Read the monthly prompt</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-serif text-lws-green mb-2">03</div>
              <h4 className="font-semibold text-lws-ink mb-1">Write</h4>
              <p className="text-sm text-lws-taupe">Sit down and begin</p>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default SubscriptionBoxPage;
